import {
  useDemoAlarmSituations,
  useIngestDemoAlarms,
} from '@hakimo-ui/hakimo/data-access';
import { DemoAlarmSituation } from '@hakimo-ui/hakimo/types';
import { eventTracker, toast, withErrorBoundary } from '@hakimo-ui/hakimo/util';
import { Alert, Button, InputField } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';
import { AlarmsPicker, PicksBySituation } from './AlarmsPicker';
import { FormSectionHeader } from './FormSectionHeader';
import { SituationsField } from './SituationsField';
import { notifyIngestResult } from './notifyIngestResult';
import { useManualAlarmIds } from './useManualAlarmIds';
import { usePickedAlarms } from './usePickedAlarms';

function DemoAlarmIngestFormComponent() {
  const {
    data: situations = [],
    isLoading: isSituationsLoading,
    isError: isSituationsError,
    error: situationsError,
  } = useDemoAlarmSituations();

  const [selectedSituations, setSelectedSituations] = useState<
    DemoAlarmSituation[]
  >([]);
  const [alarmIdsText, setAlarmIdsText] = useState('');
  const [picksBySituation, setPicksBySituation] = useState<PicksBySituation>(
    {}
  );
  // `resetKey` bumps after every settled mutation to remount
  // `SituationsField` — the only way to clear `MultiSelect`'s internal
  // search query. Scoped to that component so we don't tear down the
  // entire form subtree.
  const [resetKey, setResetKey] = useState(0);

  const { mutate, isLoading } = useIngestDemoAlarms(
    notifyIngestResult,
    () => toast('Failed to ingest alarms', { type: 'error' }),
    () => {
      setSelectedSituations([]);
      setAlarmIdsText('');
      setPicksBySituation({});
      setResetKey((k) => k + 1);
    }
  );

  const handleSituationsChange = (next: DemoAlarmSituation[]) => {
    const nextNames = new Set(next.map((s) => s.situation));
    setPicksBySituation((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([name]) => nextNames.has(name))
      )
    );
    setSelectedSituations(next);
  };

  const { pickedAlarms, allPickedAlarmIds } = usePickedAlarms(
    selectedSituations,
    picksBySituation
  );

  const manual = useManualAlarmIds(alarmIdsText, allPickedAlarmIds);
  const totalToIngest = pickedAlarms.length + manual.count;
  const canAdd = totalToIngest > 0 && !manual.hasError;

  const handleAdd = () => {
    mutate({
      situations: selectedSituations
        .map((s) => ({
          name: s.situation,
          alarmIds: picksBySituation[s.situation] ?? [],
        }))
        .filter((s) => s.alarmIds.length > 0),
      manualAlarmIds: manual.toSubmit,
    });
  };

  return (
    <div className="bg-onlight-bg-1 dark:bg-ondark-bg-1 w-full">
      <div className="mx-auto max-w-2xl">
        <div className="mt-6 sm:mt-10">
          <FormSectionHeader />

          <div className="mt-6 space-y-6">
            {isSituationsError && (
              <Alert type="error">
                {situationsError?.message ?? 'Failed to load situations.'}
              </Alert>
            )}

            {/* `min-w-0` overrides the UA `fieldset { min-inline-size:
            min-content }` (preflight doesn't reset it) — without it the
            fieldset grows past `max-w-2xl` and no `truncate` inside fires. */}
            <fieldset
              disabled={isLoading}
              className="min-w-0 space-y-6 disabled:opacity-60"
            >
              <SituationsField
                key={resetKey}
                situations={situations}
                isLoading={isSituationsLoading}
                value={selectedSituations}
                onChange={handleSituationsChange}
              />

              <AlarmsPicker
                selectedSituations={selectedSituations}
                picksBySituation={picksBySituation}
                onPicksChange={setPicksBySituation}
                manualAlarmCount={manual.count}
              />

              <InputField
                label="Pick from production — alarm UUIDs (comma or space separated)"
                type="text"
                value={alarmIdsText}
                onChange={(e) => setAlarmIdsText(e.target.value)}
                placeholder="e.g. 776fd1fe-a89b-4b4a-8b18-30d2a1f7ed4e"
                onEnter={canAdd && !isLoading ? handleAdd : undefined}
                error={manual.hasError}
                errorText={manual.errorText}
              />

              <div className="flex items-center gap-3">
                <Button
                  variant="primary"
                  onClick={handleAdd}
                  onSideEffect={eventTracker('submit_demo_alarm_ingest')}
                  loading={isLoading}
                  disabled={!canAdd || isLoading}
                >
                  Ingest {totalToIngest} alarm{totalToIngest === 1 ? '' : 's'}
                </Button>
              </div>
            </fieldset>
          </div>
        </div>
      </div>
    </div>
  );
}

export const DemoAlarmIngestForm = withErrorBoundary(
  DemoAlarmIngestFormComponent
);

export default DemoAlarmIngestForm;
